package br.com.htms.inspeccaocampo.ui.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AccountBox
import androidx.compose.material.icons.filled.Build
import androidx.compose.material.icons.filled.DateRange
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Home
import androidx.compose.material.icons.filled.List
import androidx.compose.material.icons.filled.Person
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material.icons.filled.Star
import androidx.compose.material.icons.filled.Warning
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import br.com.htms.inspeccaocampo.data.colaboradores.ColabEscopo
import br.com.htms.inspeccaocampo.data.colaboradores.ColabVisao
import androidx.compose.ui.graphics.vector.ImageVector
import br.com.htms.inspeccaocampo.form.AppController
import br.com.htms.inspeccaocampo.form.AppTab
import br.com.htms.inspeccaocampo.ui.kit.PortalMenuItem
import br.com.htms.inspeccaocampo.ui.kit.PortalModulo

/** Abas da matriz QSMS (parte superior do menu unificado). */
enum class ColabAba(val rotulo: String, val icone: ImageVector) {
    PAINEL("Painel", Icons.Filled.Home),
    DASHBOARD("Dashboard", Icons.Filled.Star),
    COLABORADORES("Colaboradores", Icons.Filled.Person),
    VENCIMENTOS("Vencimentos", Icons.Filled.DateRange),
    CNH("CNH", Icons.Filled.AccountBox),
    PENDENCIAS("Pendências", Icons.Filled.Warning),
    REGRAS("Regras de validade", Icons.Filled.Settings),
    BACKUP("Backup e importação", Icons.Filled.Refresh)
}

/** Itens de emissão de documentos (abaixo do separador). */
object DocumentosMenu {
    const val FUNCIONARIOS = "DOC_FUNCIONARIOS"
    const val OS = "DOC_OS"
    const val EPI = "DOC_EPI"
    const val CERTIFICADO = "DOC_CERTIFICADO"
    const val APR = "DOC_APR"
    const val DASHBOARD = "DOC_DASHBOARD"
    const val IMPORTAR = "DOC_IMPORTAR"

    fun itens(): List<PortalMenuItem> = listOf(
        PortalMenuItem(OS, "Ordem de Serviço", Icons.Filled.List),
        PortalMenuItem(EPI, "Ficha de EPI", Icons.Filled.Build),
        PortalMenuItem(CERTIFICADO, "Certificados", Icons.Filled.AccountBox),
        PortalMenuItem(APR, "APR", Icons.Filled.Warning),
        PortalMenuItem(DASHBOARD, "Dashboard de documentos", Icons.Filled.Star),
        PortalMenuItem(IMPORTAR, "Importar planilha", Icons.Filled.List)
    )

    fun chave(tab: AppTab): String = when (tab) {
        AppTab.FUNCIONARIOS -> FUNCIONARIOS
        AppTab.ORDEM_SERVICO -> OS
        AppTab.FICHA_EPI -> EPI
        AppTab.CERTIFICADO -> CERTIFICADO
        AppTab.APR -> APR
        else -> FUNCIONARIOS
    }
}

object ColabQsmsMenu {
    const val SEP_DOCS = "__SEP_DOCS__"

    fun itens(visao: ColabVisao? = null): List<PortalMenuItem> {
        val base = ColabAba.entries.map { PortalMenuItem(it.name, it.rotulo, it.icone) } +
            PortalMenuItem(SEP_DOCS, "Emissão de Documentos", Icons.Filled.Edit, separador = true) +
            DocumentosMenu.itens()
        if (visao == null) return base
        val visiveis = base.filter { it.separador || visao.mostra(it.chave) }
        val temEmissao = visiveis.any { it.chave.startsWith("DOC_") }
        return if (temEmissao) visiveis else visiveis.filterNot { it.separador }
    }

    fun navegar(controller: AppController, chave: String) {
        when (chave) {
            SEP_DOCS -> Unit
            DocumentosMenu.FUNCIONARIOS -> controller.abrirColaboradores("COLABORADORES")
            DocumentosMenu.OS -> controller.abrirOrdemServico()
            DocumentosMenu.EPI -> controller.abrirFichaEpi()
            DocumentosMenu.CERTIFICADO -> controller.abrirCertificados()
            DocumentosMenu.APR -> controller.abrirApr()
            DocumentosMenu.DASHBOARD -> controller.abrirFuncionarios("DASHBOARD")
            DocumentosMenu.IMPORTAR -> controller.abrirFuncionarios("IMPORTAR")
            else -> if (ColabAba.entries.any { it.name == chave }) {
                controller.abrirColaboradores(chave)
            } else Unit
        }
    }
}

/** Shell do módulo Colaboradores QSMS (matriz + emissão de documentos). */
@Composable
fun ColabQsmsModulo(
    controller: AppController,
    titulo: String,
    selecionado: String,
    extraCabecalho: @Composable RowScope.() -> Unit = {},
    conteudo: @Composable ColumnScope.(largo: Boolean) -> Unit
) {
    val user by controller.authUser.collectAsState()
    val config by controller.appConfig.collectAsState()
    val visao = ColabEscopo.visao(user, config)
    PortalModulo(
        titulo = titulo,
        itens = ColabQsmsMenu.itens(visao),
        selecionado = selecionado,
        onSelect = { ColabQsmsMenu.navegar(controller, it) },
        extraCabecalho = extraCabecalho,
        conteudo = conteudo
    )
}

/** Atalho das telas de documento: mesmo menu, seleção no item de emissão. */
@Composable
fun DocumentosModulo(
    controller: AppController,
    tab: AppTab,
    titulo: String,
    selecionado: String? = null,
    extraCabecalho: @Composable RowScope.() -> Unit = {},
    embutido: Boolean = false,
    conteudo: @Composable ColumnScope.(largo: Boolean) -> Unit
) {
    if (embutido) {
        Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            conteudo(true)
        }
        return
    }
    ColabQsmsModulo(
        controller = controller,
        titulo = titulo,
        selecionado = selecionado ?: DocumentosMenu.chave(tab),
        extraCabecalho = extraCabecalho,
        conteudo = conteudo
    )
}